import React, { useMemo, useState, useCallback, useEffect, Suspense, lazy } from 'react';
import { Switch, Route, useLocation, Redirect } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as authSelectors from '../../../store/selectors/authSelectors';
import * as adminSelectors from '../../../store/selectors/adminSelectors';
import * as guestsSelectors from '../../../store/selectors/guestsSelectors';
import { headerHeight, footerHeight } from '../../../utils/css/sizes';
import FilterHeader from '../../Admin/FilterHeader';
import { SalonSearchMapContainer, PageBody } from './SalonSearchStyle';
import { ISalonSearchMap } from '../../../interfaces/guests';
import useFilters from '../../common/useFilters';
import useSearch from '../../common/useSearch';
import useFilterRequest from '../../../utils/useFilterRequest';
import { accessibility_searchmap, available_places, time, is_digital } from '../SalonSearchMap/searchMapFiltersList';
import { getLanguageObj, getCityObj, getDateObj } from '../../../utils/createFilterObjects';
import { getLanguages, getAvailableSalonsListInit } from '../../../store/actions/guest/salonSearchMap.action';
import { getCities } from '../../../store/actions/admin/admin.action';
import useWindowSize from '../../common/useWindowSize';
import { useTranslation } from 'react-i18next';
import { getGuestProfileById, getGuestSalonById } from '../../../store/actions/guest/guest.action';
import Loader from '../../common/Loader';
import { routes } from '../../../utils/constants/routes';
import { createTableFiltersRequest } from '../../../utils/createTableFiltersRequest';
import {useIsMobile} from '../../../utils/usIsMobile';
import LoaderTitle from '../../common/LoaderTitle/LoaderTitle';

const SalonSearchMap = lazy(() => import('../SalonSearchMap'));
const GuestDigitalSalon = lazy(() => import('../GuestDigitalSalon'));

const SalonSearch: React.FC<ISalonSearchMap> = (props) => {
  const { history, match } = props;
  const location = useLocation();
  const actionDispatch = useDispatch();

  const guest = useSelector(authSelectors.guestSelector);

  const cities = useSelector(adminSelectors.citiesSelector);
  const languages = useSelector(adminSelectors.languagesSelector);
  const salondays = useSelector(adminSelectors.salondaysSelector);

  const availableSalonsList = useSelector(guestsSelectors.availableSalonsListSelector);
  const previousDigitalSalonFilters = useSelector(guestsSelectors.previousDigitalSalonFiltersSelector);
  const nextDigitalSalonFilters = useSelector(guestsSelectors.nextDigitalSalonFiltersSelector);

  const [selectedSalonData, setSelectedSalonData] = useState({});
  const [filtersHeight, setFiltersHeight] = useState<number>(0);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(true);
  const [showPhysicalSalon, setShowPhysicalSalon] = useState(false);
  const [isAfterInitialRender, setIsAfterInitialRender] = useState<boolean>(false);
  const [isInitialRender, setIsInitialRender] = useState<boolean>(true);
  const isMobile = useIsMobile();
  useEffect(() => {
    actionDispatch(getLanguages());
    actionDispatch(getCities());
    guest && actionDispatch(getGuestProfileById(guest.id)) && actionDispatch(getGuestSalonById(guest.id));
  }, [actionDispatch, guest]);

  //should show a button to swich between table to map display
  useEffect(() => {
    if (location.state) {
      // @ts-ignore
      if (location.state.url === routes.salonSearch.mapView) {
        setShowPhysicalSalon(true);
      } else {
        setShowPhysicalSalon(false);
      }
    }
  }, [location]);

  const { t } = useTranslation();

  useEffect(() => {
    if (match.params.id && availableSalonsList.length) {
      return setSelectedSalonData(availableSalonsList[0]);
    } else return setSelectedSalonData({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSalonsList]);

  const getInitialState = () => {
    return {
      city: getCityObj(cities),
      language: getLanguageObj(languages),
      available_places,
      accessibility_searchmap,
      date: getDateObj(salondays),
      time,
      is_digital,
    };
  };
  const dep = [languages, cities, salondays];
  const { setFiltersState, ...filterProps } = useFilters({
    initialState: getInitialState(),
    dep,
  });

  const { debouncedValue, searchValue, ...searchProps } = useSearch();
  const [width] = useWindowSize();

  const onFiltersHeightChange = (height: number) => {
    setFiltersHeight(height);
  };

  const pageHeight = useMemo(() => {
    if (isMobile) {
      return `calc(100vh - 350px)`;
    }
    const headerFilterHeight = headerHeight + footerHeight;
    return `calc(100vh - 250px)`;
  }, [isFiltersOpen, filtersHeight]);

  const getHostSalonsByFilters = useCallback(
    (params: { [key: string]: any }) => {
      if (Object.keys(params).length) {
        setIsAfterInitialRender(true);
      }
      const path =window.location.pathname;
      const IdInPath = path.match(/\/(\d+)$/);
      if(IdInPath) params = {...params, id: IdInPath[1]}

      if (isInitialRender || isAfterInitialRender) {
        setIsInitialRender(false);
        actionDispatch(getAvailableSalonsListInit(params));
      }
    },

    [actionDispatch, location, isAfterInitialRender, isInitialRender]
  );

  useFilterRequest(filterProps.state, filterProps.setNumOfActiveFilters, getHostSalonsByFilters);

  useEffect(() => {
    filterProps.state.city &&
      filterProps.state.city.options.length &&
      setFiltersState({ ...filterProps.state, debouncedValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, cities]);

  // const navigateToUserProfile = useCallback(() => {
  //   !isUserAdmin
  //     ? history.push(buildPath(routes.guestProfile.profile, { id: guest.id }))
  //     : history.push(buildPath('/admin'));
  // }, [guest, history, isUserAdmin]);

  const { navigationLink } = useMemo(() => {
    if (location.pathname === routes.salonSearch.salonTable) {
      return {
        navigationLink: routes.salonSearch.mapView,

        navigationButtonText: t('guests.salonSearchMap.map'),
      };
    }
    return {
      navigationLink: routes.salonSearch.salonTable,
    };
  }, [location.pathname, t]);

  const changeSalonsDisplay = useCallback(() => {
    history.push(navigationLink);
  }, [history, navigationLink]);

  const clearFilters = useCallback(() => {
    filterProps.clearAllFilters();
    searchProps.clearSearchValue();
  }, [filterProps, searchProps]);

  const onChangeTablePage = useCallback(
    (pageNumber: number) => {
      createTableFiltersRequest(
        nextDigitalSalonFilters,
        previousDigitalSalonFilters,
        pageNumber,
        getHostSalonsByFilters
      );
    },
    [previousDigitalSalonFilters, getHostSalonsByFilters, nextDigitalSalonFilters]
  );

  return (
    <SalonSearchMapContainer height={pageHeight}>
      {/* {width >= mobileWidth ? ( */}
      <FilterHeader
        {...filterProps}
        {...searchProps}
        debouncedValue={debouncedValue}
        searchValue={searchValue}
        onHeightChange={onFiltersHeightChange}
        isFiltersOpen={isFiltersOpen}
        setIsFiltersOpen={setIsFiltersOpen}
      />
      {/* ) : (
        <FilterHeaderMobile
          {...filterProps}
          {...searchProps}
          searchValue={searchValue}
          debouncedValue={debouncedValue}
        />
      )} */}
      <PageBody height={filtersHeight} isOpen={isFiltersOpen}>
        <Suspense fallback={
          <div>
          <Loader />
          <LoaderTitle />
          </div>
      }>
          <Switch>
            <Route path={routes.salonSearch.mapView} exact render={() => <SalonSearchMap pageHeight={pageHeight} />} />
            <Route
              path={routes.salonSearch.salonTable}
              exact
              render={() => (
                <GuestDigitalSalon
                  filtersCount={filterProps.numOfActiveFilters}
                  clearFilters={clearFilters}
                  onChangeTablePage={onChangeTablePage}
                  isShowPhysicalSalon={showPhysicalSalon}
                />
              )}
            />
            <Route
              path={routes.salonSearch.oneSalonMap}
              exact
              render={() => <SalonSearchMap pageHeight={pageHeight} />}
            />
            {// @ts-ignore
            location.state && <Redirect to={location.state.url} />}
          </Switch>
        </Suspense>
      </PageBody>
    </SalonSearchMapContainer>
  );
};

export default SalonSearch;
